import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import Moment from 'react-moment';

const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<]+|[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?:\/[^\s<]*)?)/gi;

const REACTIONS = [
    { type: "love", icon: "❤️", label: "Yêu thích" },
    { type: "haha", icon: "😂", label: "Ha ha" },
    { type: "wow", icon: "😮", label: "Wow" },
    { type: "sad", icon: "😢", label: "Buồn" },
    { type: "angry", icon: "😡", label: "Phẫn nộ" },
    { type: "like", icon: "👍", label: "Thích" }
];

function renderTextWithLinks(text, linkClassName) {
    if (typeof text !== 'string' || text === "") return text;

    const parts = [];
    let lastIndex = 0;
    let match;
    URL_REGEX.lastIndex = 0;

    while ((match = URL_REGEX.exec(text)) !== null) {
        let url = match[0];
        const trailing = url.match(/[).,;:!?]+$/);
        if (trailing) url = url.slice(0, url.length - trailing[0].length);
        if (url === "") continue;

        const start = match.index;
        if (start > lastIndex) parts.push(text.slice(lastIndex, start));

        const href = /^https?:\/\//i.test(url) ? url : "http://" + url;
        parts.push(
            <a key={start} href={href} target="_blank" rel="noopener noreferrer" className={linkClassName}>
                {url}
            </a>
        );
        lastIndex = start + url.length;
    }

    if (parts.length === 0) return text;
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
}

function normalizeMediaItem(item) {
    if (typeof item === 'string') return { url: item };
    if (!item || typeof item !== 'object') return null;
    const url = item.url || item.path || item.originalUrl;
    return url ? { ...item, url } : null;
}

function parseMediaValue(raw) {
    if (Array.isArray(raw)) return raw.map(normalizeMediaItem).filter(Boolean);
    if (raw && typeof raw === 'object') {
        return Object.keys(raw).map(key => normalizeMediaItem(raw[key])).filter(Boolean);
    }
    if (typeof raw !== 'string') return [];

    const trimmed = raw.trim();
    if (/^https?:\/\//i.test(trimmed)) return [{ url: trimmed }];
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return [];
    try {
        return parseMediaValue(JSON.parse(trimmed));
    } catch (error) {
        console.error("Không parse được media của tin nhắn:", error);
        return [];
    }
}

function getMessageLabel(message) {
    if (!message) return "Tin nhắn";
    if (message.status === "RECALL") return "Tin nhắn đã được thu hồi";
    switch (message.type) {
        case "image": return "Hình ảnh";
        case "video": return "Video";
        case "voice": return "Tin nhắn thoại";
        case "audio": return "File ghi âm";
        default: return message.message || message.originalMessage || "Tin nhắn";
    }
}

function getMediaType(messageDetail, mediaItems) {
    if (messageDetail.status === "RECALL" && ["image", "video", "voice", "audio"].includes(messageDetail.oldType)) {
        return messageDetail.oldType;
    }
    if (messageDetail.status !== "RECALL" && ["image", "video", "voice", "audio"].includes(messageDetail.type)) {
        return messageDetail.type;
    }

    const firstMedia = mediaItems[0];
    if (!firstMedia) return messageDetail.type;
    if (["image", "video", "voice", "audio"].includes(firstMedia.type)) return firstMedia.type;

    const url = (firstMedia.url || "").split("?")[0].toLowerCase();
    if (/\.(mp4|mov|webm|mkv)$/.test(url)) return "video";
    if (/\.(m4a|mp3|aac|wav|ogg|opus|flac)$/.test(url)) return "audio";
    return "image";
}

export default class MessageDetailItem extends Component {
    constructor(props) {
        super(props);
        this.state = { showMenu: false, previewImage: null };
        this.toggleMenu = this.toggleMenu.bind(this);
        this.renderContent = this.renderContent.bind(this);
        this.renderReactionSummary = this.renderReactionSummary.bind(this);
    }

    toggleMenu(event) {
        event.stopPropagation();
        this.setState(prevState => ({ showMenu: !prevState.showMenu }));
    }

    getMediaItems(messageDetail) {
        const declaredType = messageDetail.status === "RECALL"
            ? messageDetail.oldType
            : messageDetail.type;

        // URL nằm trong tin nhắn text phải được hiển thị thành liên kết, không
        // được suy đoán thành ảnh chỉ vì nội dung bắt đầu bằng http/https.
        if (declaredType === "text") return [];

        const candidates = [
            messageDetail.media,
            messageDetail.images,
            messageDetail.oldMessage,
            messageDetail.originalMessage,
            messageDetail.message
        ];
        for (let i = 0; i < candidates.length; i++) {
            const media = parseMediaValue(candidates[i]);
            if (media.length > 0) return media;
        }
        return [];
    }

    renderParentPreview() {
        const { messageDetail } = this.props;
        if (!messageDetail.parentId) return null;

        return (
            <button
                type="button"
                className="reply-preview-in-message"
                title="Đi tới tin nhắn gốc"
                onClick={() => {
                    const parentElement = document.getElementById(`message-${messageDetail.parentId}`);
                    if (parentElement) parentElement.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
            >
                <span className="reply-preview-line" />
                <span>{getMessageLabel(messageDetail.parent)}</span>
            </button>
        );
    }

    renderContent(isMine) {
        const { messageDetail } = this.props;
        const isRecalled = messageDetail.status === "RECALL";
        const mediaItems = this.getMediaItems(messageDetail);
        const type = getMediaType(messageDetail, mediaItems);
        let content;

        if (type === "image" && mediaItems.length > 0) {
            content = (
                <div className="chat-media-grid">
                    {mediaItems.map((media, index) => (
                        <button
                            key={`${media.url}-${index}`}
                            type="button"
                            className="chat-image-button"
                            title="Xem ảnh đầy đủ"
                            onClick={() => this.setState({ previewImage: media.url })}
                        >
                            <img
                                className="chat-image"
                                src={media.url}
                                alt="Hình ảnh trong tin nhắn"
                                onError={(event) => {
                                    event.target.style.display = 'none';
                                    if (event.target.parentNode) event.target.parentNode.classList.add('chat-image-broken');
                                }}
                            />
                        </button>
                    ))}
                </div>
            );
        } else if (type === "video" && mediaItems.length > 0) {
            content = mediaItems.map((media, index) => (
                <div className="chat-media-player" key={`${media.url}-${index}`}>
                    <video controls preload="metadata" poster={media.thumbnailUrl || undefined} src={media.url}>
                        Trình duyệt không hỗ trợ phát video này.
                    </video>
                </div>
            ));
        } else if ((type === "voice" || type === "audio") && mediaItems.length > 0) {
            content = mediaItems.map((media, index) => (
                <div className="chat-audio-player" key={`${media.url}-${index}`}>
                    <div className="chat-audio-icon">
                        <i className={`fa ${type === "voice" ? "fa-microphone" : "fa-music"}`} aria-hidden="true" />
                    </div>
                    <div className="chat-audio-content">
                        <span>{type === "voice" ? "Tin nhắn thoại" : "File ghi âm"}</span>
                        {/* App lưu mimeType là "voice"/"audio", không phải MIME chuẩn.
                            Dùng src trực tiếp để trình duyệt tự nhận diện định dạng file. */}
                        <audio controls preload="metadata" src={media.url}>
                            Trình duyệt không hỗ trợ phát file ghi âm này.
                        </audio>
                    </div>
                </div>
            ));
        } else {
            let text = isRecalled ? messageDetail.oldMessage : messageDetail.message;
            if (text == null || text === "" || text === "[recalled_message]") {
                text = isRecalled
                    ? "Không còn dữ liệu nội dung trước khi thu hồi."
                    : messageDetail.originalMessage;
            }
            content = (
                <span className="message-text">
                    {renderTextWithLinks(text, isMine && !isRecalled ? "chat-link chat-link-light" : "chat-link")}
                </span>
            );
        }

        if (!isRecalled) return content;
        return (
            <div className="recalled-message-content">
                <span className="recalled-message">
                    <i className="fa fa-undo" aria-hidden="true" /> Đã thu hồi
                </span>
                <div className="recalled-original-content">{content}</div>
            </div>
        );
    }

    renderReactionSummary() {
        const { messageDetail, currentUserId, onReact } = this.props;
        const reactions = messageDetail.reactions;
        if (!reactions || !reactions.details) return null;

        const activeReaction = reactions.users && reactions.users[currentUserId];
        const items = REACTIONS.map(reaction => {
            const users = reactions.details[reaction.type];
            const count = users ? Object.keys(users).length : 0;
            return { ...reaction, count };
        }).filter(reaction => reaction.count > 0);

        if (items.length === 0) return null;
        return (
            <div className="message-reaction-summary">
                {items.map(reaction => (
                    <button
                        key={reaction.type}
                        type="button"
                        className={activeReaction === reaction.type ? "active" : ""}
                        title={reaction.label}
                        onClick={() => onReact(messageDetail, reaction.type)}
                    >
                        <span>{reaction.icon}</span> {reaction.count}
                    </button>
                ))}
            </div>
        );
    }

    renderReactionPicker() {
        const { messageDetail, onReact, currentUserId } = this.props;
        const currentReaction = messageDetail.reactions
            && messageDetail.reactions.users
            && messageDetail.reactions.users[currentUserId];

        return (
            <div className="message-reaction-picker">
                {REACTIONS.map(reaction => (
                    <button
                        key={reaction.type}
                        type="button"
                        title={reaction.label}
                        className={currentReaction === reaction.type ? "active" : ""}
                        onClick={() => onReact(messageDetail, reaction.type)}
                    >
                        {reaction.icon}
                    </button>
                ))}
            </div>
        );
    }

    renderHoverTools(canRecall) {
        const { messageDetail, onReply, onRecall } = this.props;

        return (
            <div className="message-hover-tools">
                <div className="message-more-wrap">
                    <button
                        type="button"
                        className={`message-tool-btn ${this.state.showMenu ? "active" : ""}`}
                        title="Thao tác khác"
                        aria-label="Thao tác khác"
                        aria-expanded={this.state.showMenu}
                        onClick={this.toggleMenu}
                    >
                        <i className="fa fa-ellipsis-h" aria-hidden="true" />
                    </button>
                    {this.state.showMenu && (
                        <div className="message-more-menu">
                            <button
                                type="button"
                                onClick={() => {
                                    this.setState({ showMenu: false });
                                    onReply(messageDetail);
                                }}
                            >
                                <i className="fa fa-reply" aria-hidden="true" /> Phản hồi
                            </button>
                            {canRecall && (
                                <button
                                    type="button"
                                    className="danger"
                                    onClick={() => {
                                        this.setState({ showMenu: false });
                                        onRecall(messageDetail);
                                    }}
                                >
                                    <i className="fa fa-undo" aria-hidden="true" /> Thu hồi
                                </button>
                            )}
                            <span className="message-menu-time">
                                <Moment withTitle format="DD/MM/YYYY HH:mm">{messageDetail.dateTime}</Moment>
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { messageDetail, targetUser, currentUserId } = this.props;
        if (!messageDetail || !targetUser) return null;

        // Chỉ tin có userId trùng tài khoản Firebase hiện tại mới là tin của mình.
        // Cách cũ coi mọi tin không phải của người đang chat là tin của mình, dẫn tới
        // hiện nút thu hồi cho tin của tài khoản khác và bị backend từ chối quyền.
        const isMine = messageDetail.userId != null
            && currentUserId != null
            && String(messageDetail.userId) === String(currentUserId);
        const canInteract = messageDetail.status !== "RECALL";
        const messageId = messageDetail.key || messageDetail.firebaseId || "unknown";
        const avatar = messageDetail.userProfile || targetUser.userProfile;

        return (
            <div
                id={`message-${messageId}`}
                className={`message-row-wrapper ${isMine ? "mine" : "theirs"}`}
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) this.setState({ showMenu: false });
                }}
            >
                <div className="message-row-shell">
                    {!isMine && <img src={avatar} alt="" width={32} height={32} className="message-avatar" />}
                    {isMine && canInteract && this.renderHoverTools(true)}
                    <div className="message-bubble-column">
                        {canInteract && this.renderReactionPicker()}
                        <div className={`bubble ${isMine ? "bubble-right" : "bubble-left"} ${canInteract ? "" : "is-recalled"}`}>
                            {this.renderParentPreview()}
                            {messageDetail.senderType === 'BOT' && (
                                <span className="message-bot-label" title={messageDetail.botSendType === 'HANDOVER' ? 'BOT gửi câu chuyển nhân viên' : 'Tin nhắn tự động từ BOT'}>
                                    BOT
                                </span>
                            )}
                            {this.renderContent(isMine)}
                        </div>
                        {this.renderReactionSummary()}
                    </div>
                    {!isMine && canInteract && this.renderHoverTools(false)}
                </div>
                {this.state.previewImage && createPortal(
                    <div
                        className="chat-image-viewer"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Xem ảnh đầy đủ"
                        onClick={() => this.setState({ previewImage: null })}
                        onKeyDown={(event) => {
                            if (event.key === "Escape") this.setState({ previewImage: null });
                        }}
                    >
                        <div className="chat-image-viewer-content" onClick={(event) => event.stopPropagation()}>
                            <button
                                type="button"
                                className="chat-image-viewer-close"
                                title="Đóng"
                                aria-label="Đóng"
                                autoFocus
                                onClick={() => this.setState({ previewImage: null })}
                            >
                                <i className="fa fa-times" aria-hidden="true" />
                            </button>
                            <img src={this.state.previewImage} alt="Ảnh đầy đủ trong tin nhắn" />
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        );
    }
}
